import { observer } from 'mobx-react';
import { FC, ReactElement, useEffect, useMemo } from 'react';
import { Prompt, StaticContext } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import WebhookEditMainBlock from './containers/WebhookEditMainBlock/WebhookEditMainBlock';
import WebhookEditStatusesBlock from './containers/WebhookEditStatusesBlock/WebhookEditStatusesBlock';
import WebhookParameters from './containers/WebhookParameters/WebhookParameters';
import { webhookEditStore } from './store';
import { SCREENS } from '~/constants/screens';
import { getPromptProps } from '~/view/components/CustomPrompt/helper';
import LoadingOverlay from '~/view/components/LoadingOverlay/LoadingOverlay';
import HeaderBack from '~/view/containers/Headers/components/HeaderBack/HeaderBack';
import Headers from '~/view/containers/Headers/Headers';
import ScrollableBlock from '~/view/containers/ScrollableBlock/ScrollableBlock';
import SettingControlsBlock from '~/view/screens/SettingsScreen/containers/SettingControlsBlock/SettingControlsBlock';
import { getBackStepPath } from '~/helpers';

type TProps = RouteComponentProps<{ id?: string }, StaticContext, undefined>;

const WebhookEditScreen: FC<TProps> = observer((props: TProps): ReactElement => {
  useEffect(() => {
    webhookEditStore.initStore(props.match.params.id);
  }, []);

  const title = useMemo((): string => (props.match.params.id ? 'edit_webhook' : 'create_new_webhook'), [props.match.params.id]);

  return (
    <div className="d-flex flex-column flex-1">
      <Headers>
        <HeaderBack title={title} path={getBackStepPath(SCREENS.SETTINGS_WEBHOOK())} isBeta />
      </Headers>

      <ScrollableBlock showScrollbar scrollbarSize="normal" className="flex-1 mt-6" scrollClassName="d-flex flex-column gap-30">
        <WebhookEditMainBlock />
        <WebhookParameters
          title="headers"
          params={webhookEditStore.webhook.headers}
          paramsValidation={webhookEditStore.validation.headers}
          addParam={webhookEditStore.addHeadersParam}
          deleteParam={webhookEditStore.deleteHeadersParam}
          editParam={webhookEditStore.editHeadersParam}
        />
        <WebhookParameters
          title="parameters"
          params={webhookEditStore.webhook.parameters}
          paramsValidation={webhookEditStore.validation.parameters}
          addParam={webhookEditStore.addParametersParam}
          deleteParam={webhookEditStore.deleteParametersParam}
          editParam={webhookEditStore.editParametersParam}
        />
        <WebhookEditStatusesBlock />

        <SettingControlsBlock
          redirectPath={SCREENS.SETTINGS_WEBHOOK()}
          setValidation={webhookEditStore.setValidation}
          onSubmit={webhookEditStore.submit}
          onReset={webhookEditStore.resetWebhook}
        />
      </ScrollableBlock>

      <LoadingOverlay visible={webhookEditStore.isLoading} />
      <Prompt when={webhookEditStore.isEdit} message={getPromptProps()} />
    </div>
  );
});

export default WebhookEditScreen;
